import { useEffect } from 'react'

import { EXTREME_UPGRADED_EVENT, primeExtremeAdvancedCache } from 'src/constants/extremeUpgrade'

/**
 * 订阅 Extreme 升级成功事件，刷新「当前所在功能页」的数据与 advanced 状态。
 *
 * 使用场景：页面内嵌了 ExtremeModal，用户在不离开页面的情况下完成升级后，
 * 需要去掉模糊遮罩并重新请求本页接口（如 smsChat-summary / getAllDashboard 等）。
 *
 * 刷新顺序（与 spyx / 后端缓存策略对齐）：
 * 1. notifyExtremeUpgraded 内已 await USER_SUMMARY 预缓冲（触发后台 advanced 同步）
 * 2. 本 hook 再 await primeExtremeAdvancedCache（二次预热，读到同步后的 advanced）
 * 3. 再执行 onUpgraded → 本页业务详情接口，用返回的 advanced 解锁 UI
 *
 * 注意：
 * - onUpgraded 内应复用「设备切换」时的重置 + 拉数逻辑（与 useDeviceScopedInit 保持一致）
 * - 建议先 setAdvanced(true) 乐观解锁 UI，再 dispatch 拉真实数据
 * - onUpgraded 可为 async；须 useCallback 包裹，避免重复注册监听
 *
 * @param {() => void | Promise<void>} onUpgraded - 升级成功后的刷新回调
 *
 * @example
 * useExtremeUpgradeRefresh(
 *   useCallback(() => {
 *     setAdvanced(true)
 *     dispatch(fetchUserProfile()) // hook 内已先 USER_SUMMARY，再执行此处
 *   }, [dispatch])
 * )
 */
export function useExtremeUpgradeRefresh(onUpgraded) {
  useEffect(() => {
    if (!onUpgraded) return

    const handler = async () => {
      await primeExtremeAdvancedCache()
      await Promise.resolve(onUpgraded())
    }

    window.addEventListener(EXTREME_UPGRADED_EVENT, handler)

    return () => window.removeEventListener(EXTREME_UPGRADED_EVENT, handler)
  }, [onUpgraded])
}

export default useExtremeUpgradeRefresh
